import { Center, Checkbox, Text, Title } from "@mantine/core"
import { useMutation } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { ActivityImage } from "@/components/activities"
import { Page404, PageLoader } from "@/components/utils"
import { useAPIResource, useAPIResources } from "@/hooks"
import { activitiesAPI, dayjs, questionsAPI, setQueryCache, usersessionAPI } from "@/utils"
import { ButtonSurvey } from "./StageButton"
import { StageControls } from "./StageControls"
import type { IStageProps } from "./types"

export default function WelcomeStage({ usersession, onNext }: IStageProps) {
  const { t } = useTranslation()
  const { session } = usersession

  // Load Data
  const { data: activity, isPending: isActivityPending } = useAPIResource(activitiesAPI, {
    id: usersession.activity_id,
    ...setQueryCache(3600e3),
  })
  const { data: { data: questions = [] } = {}, isPending: isQuestionsPending } = useAPIResources(questionsAPI, {
    where: { session_id: usersession.session.id },
  })
  const [isAnonymous, setAnonymous] = useState(false)
  const update = useMutation({
    mutationFn: () => usersessionAPI.update!({ is_anonymous: isAnonymous, id: usersession.id }),
  })
  const dtnow = Date.now()
  const isStarted = session.dtstart <= dtnow

  useEffect(() => {
    if (!isStarted) return
    if (!isQuestionsPending && !questions.length) onNext()
  }, [isStarted, questions, isQuestionsPending])

  if (isQuestionsPending || isActivityPending) return <PageLoader />
  if (!activity) return <Page404 />

  const isAnonymousAvailable = usersession.is_anonymous
  const buttonMR = isAnonymousAvailable ? 0 : "auto"

  return (
    <>
      <ActivityImage activity={activity} h={200} radius="lg" />
      <Title order={2} miw={500}>
        {session.title}
      </Title>
      {activity.subtitle?.length > 0 && <Text size="lg">{activity.subtitle}</Text>}

      <StageControls>
        {isAnonymousAvailable && (
          <Center>
            <Checkbox
              size="lg"
              label={t("Participate anonymously")}
              checked={isAnonymous}
              onChange={(e) => {
                setAnonymous(e.currentTarget.checked)
              }}
            />
          </Center>
        )}
        {isStarted ? (
          <ButtonSurvey
            ml="auto"
            mr={buttonMR}
            variant="save"
            onClick={() => {
              update.mutate()
              if (onNext) onNext()
            }}
          >
            {t("Start the survey")}
          </ButtonSurvey>
        ) : (
          <ButtonSurvey variant="save" disabled ml="auto" mr={buttonMR}>
            {dayjs(session.dtstart).format("DD MMMM YYYY HH:mm")}
          </ButtonSurvey>
        )}
      </StageControls>
    </>
  )
}
